import { Message } from "@twilio/conversations";
import { minutesToMilliseconds } from "date-fns";
import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { NewMessageToast } from "../../components/NewMessageToast";
import { logger } from "../../datadog/logger";
import { TrackingEvents, sendLoggingEvents } from "../../events";
import {
  chatParticipantsByIdAtom,
  unreadMessageCountByConversationSidAtom,
} from "../../session/state";
import { chatParticipantToName } from "../../session/utils";
import { toast } from "../../toasts";
import { isScreenSharingAtom } from "../../video-sdk";
import { twilioIdentityAtom } from "../../video-sdk/twilio";
import {
  ChatDrawerState,
  ProviderDrawerState,
  drawerStateAtom,
} from "../state";
import { NewWaitingRoomMessageToast } from "./NewWaitingRoomMessageToast";
import { useOpenConversationCallback } from "./useOpenConversationCallback";

const TOAST_TIMEOUT = minutesToMilliseconds(1);

export function getToastTagForConversation(sid: string) {
  return `NEW_MESSAGE_TOAST:conversation:${sid}`;
}

export function useShowNewMessageToastCallback() {
  const setUnreadMessageCount = useSetAtom(
    unreadMessageCountByConversationSidAtom,
  );
  const getChatParticipants = useAtomCallback(
    useCallback((get) => get(chatParticipantsByIdAtom), []),
  );
  const openConversation = useOpenConversationCallback();
  const getDrawerState = useAtomCallback(
    useCallback((get) => get(drawerStateAtom), []),
  );
  const getTwilioIdentity = useAtomCallback(
    useCallback((get) => get(twilioIdentityAtom), []),
  );
  const getIsLocalScreenShareMode = useAtomCallback(
    useCallback((get) => get(isScreenSharingAtom), []),
  );

  return useCallback(
    (message: Message, conversationKind: ChatDrawerState) => {
      const isConversationOpen = getDrawerState() === conversationKind;
      const isSelf = message.author === getTwilioIdentity();
      const isLocalScreenShareMode = getIsLocalScreenShareMode();

      if (isSelf || isConversationOpen || isLocalScreenShareMode) return;

      const participants = getChatParticipants();
      const participant = message.author ? participants[message.author] : null;
      const conversationSid = message.conversation.sid;
      let senderName = "New message";
      if (participant) {
        senderName = chatParticipantToName(participant);
      } else {
        const { author, sid } = message;
        logger.error("Could not find participant for message", {
          author,
          messageSid: sid,
          conversationSid,
          chatParticipantSids: Object.keys(participants),
        });
      }

      const sendQuickReply = async (
        newMessage: string,
        closeToast: () => void,
      ) => {
        await message.conversation.sendMessage(newMessage);
        sendLoggingEvents(TrackingEvents.ANY_MESSAGE_SEND);
        sendLoggingEvents(TrackingEvents.ANY_WAITING_ROOM_MESSAGE_SEND);
        setUnreadMessageCount((prev) => ({
          ...prev,
          [conversationSid]: 0,
        }));
        closeToast();
        toast.success(`Message to ${senderName} sent!`, {
          purpose: "realtime",
          isMasked: true,
        });
      };

      // Dismiss any existing toasts for this conversation first.
      toast.closeByTag(getToastTagForConversation(conversationSid));

      toast.enqueue(
        ({ closeToast, ...ariaProps }) => {
          const Toast =
            conversationKind === ProviderDrawerState.WAITING_ROOM_CHAT
              ? NewWaitingRoomMessageToast
              : NewMessageToast;

          return (
            <Toast
              {...ariaProps}
              senderName={senderName}
              messageContent={message.body ?? ""}
              onOpenChatDrawer={() => {
                closeToast();
                openConversation(conversationKind, message.conversation.sid);
              }}
              onSend={(message: string) => sendQuickReply(message, closeToast)}
            />
          );
        },
        {
          purpose: "realtime",
          timeout: TOAST_TIMEOUT,
          tags: new Set([getToastTagForConversation(conversationSid)]),
        },
      );
    },
    [
      getChatParticipants,
      getDrawerState,
      getTwilioIdentity,
      getIsLocalScreenShareMode,
      openConversation,
      setUnreadMessageCount,
    ],
  );
}
